/**
 * SATCHEL — Brand values section behaviour.
 *
 * Two independent jobs, both progressive enhancements over markup that already works on
 * its own:
 *
 * 1. Text reveal. The section renders its text fully visible. This script hides it only
 *    when it is still below the viewport, then reveals it once it scrolls in. So if this
 *    file never loads nothing is hidden, and text already on screen does not flash out and
 *    back in. The stagger and the motion are pure CSS in the section.
 *
 * 2. Video autoplay. The video renders with native controls and preload="none". This
 *    script swaps the controls for a pause button and plays the video, muted, only while
 *    it is on screen. It leaves the native controls alone, and never autoplays, for
 *    visitors with reduced motion or data saver turned on. On mobile data in Egypt a
 *    video that downloads off screen is a real cost.
 */
(() => {
  if (!('IntersectionObserver' in window)) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = navigator.connection?.saveData === true;

  /* ---------------------------------------------------------------------------------- */
  /* Text reveal                                                                        */
  /* ---------------------------------------------------------------------------------- */

  const PENDING_CLASS = 'satchel-usp__content--pending';
  const REVEALED_CLASS = 'satchel-usp__content--revealed';

  // The bottom margin waits until the text is a little way into the viewport, so the
  // first lines are not already animating while they are still hidden under the fold.
  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        entry.target.classList.replace(PENDING_CLASS, REVEALED_CLASS);
        revealObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -15% 0px' }
  );

  const setupReveal = (root) => {
    if (reducedMotion.matches) return;

    root.querySelectorAll('[data-satchel-usp-reveal]').forEach((content) => {
      if (content.dataset.satchelUspReveal === 'ready') return;
      content.dataset.satchelUspReveal = 'ready';

      if (content.getBoundingClientRect().top < window.innerHeight) return;

      content.classList.add(PENDING_CLASS);
      revealObserver.observe(content);
    });
  };

  /* ---------------------------------------------------------------------------------- */
  /* Video autoplay                                                                     */
  /* ---------------------------------------------------------------------------------- */

  const PLAYING_CLASS = 'satchel-usp__video--playing';

  // A quarter of the video on screen is enough to count as being watched. Scrolling it
  // away pauses it, but does not count as the visitor pausing it, so it resumes on return.
  const videoObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target.querySelector('video');

        if (entry.isIntersecting) {
          if (entry.target.dataset.pausedByVisitor !== 'true') {
            // play() rejects if the browser blocks autoplay. The pause button is still
            // there, so the visitor can start it themselves.
            video.play().catch(() => {});
          }
        } else if (!video.paused) {
          video.pause();
        }
      }
    },
    { threshold: 0.25 }
  );

  const setupVideos = (root) => {
    if (reducedMotion.matches || saveData) return;

    root.querySelectorAll('[data-satchel-usp-video]').forEach((wrapper) => {
      if (wrapper.dataset.satchelUspVideo === 'ready') return;

      const video = wrapper.querySelector('video');
      const toggle = wrapper.querySelector('.satchel-usp__video-toggle');
      if (!video || !toggle) return;

      wrapper.dataset.satchelUspVideo = 'ready';

      const sync = () => {
        const playing = !video.paused;
        wrapper.classList.toggle(PLAYING_CLASS, playing);
        toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
      };

      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);

      toggle.addEventListener('click', () => {
        if (video.paused) {
          delete wrapper.dataset.pausedByVisitor;
          video.play().catch(() => {});
        } else {
          wrapper.dataset.pausedByVisitor = 'true';
          video.pause();
        }
      });

      video.controls = false;
      toggle.hidden = false;
      sync();
      videoObserver.observe(wrapper);
    });
  };

  /* ---------------------------------------------------------------------------------- */

  const setup = (root) => {
    setupReveal(root);
    setupVideos(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setup(document), { once: true });
  } else {
    setup(document);
  }

  // The theme editor replaces a section's HTML whenever a setting changes.
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
